import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AdSlot } from '../ads/AdSlot';
import { LEGAL } from '../pages/legal/config';
import { categoryListing, findListing } from '../games/listings';
import { CATALOG, CATEGORIES } from '../games/catalog';
import { SoundToggle } from './SoundToggle';
import { HeaderActions } from './HeaderActions';
import { LogoMark } from './Wordmark';
import { NavIcon, type IconName } from './NavIcons';

function Brand() {
  return (
    <Link to="/" className="brand" aria-label="SuperFunUBG home">
      <LogoMark size={40} />
      <span className="brand__text">
        SuperFun<span className="brand__ubg">UBG</span>
      </span>
    </Link>
  );
}

/** Toolbar search for every page except the homepage, which has its own big search bar. */
function SearchBox() {
  const { pathname } = useLocation();
  return pathname === '/' ? null : <ToolbarSearch />;
}

function ToolbarSearch() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const initial = pathname === '/' ? params.get('q') ?? '' : '';
  const [open, setOpen] = useState(initial !== '');
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  useEffect(() => {
    if (pathname !== '/') {
      setOpen(false);
      setValue('');
    }
  }, [pathname]);

  const update = (v: string) => {
    setValue(v);
    navigate(v.trim() ? `/?q=${encodeURIComponent(v.trim())}` : '/', { replace: pathname === '/' });
  };

  if (!open) {
    return (
      <button type="button" className="tool-btn" aria-label="Search games" onClick={() => setOpen(true)}>
        <SearchIcon />
      </button>
    );
  }
  return (
    <form className="search" role="search" onSubmit={(e) => e.preventDefault()}>
      <label htmlFor={id} className="visually-hidden">
        Search games
      </label>
      <SearchIcon />
      <input
        ref={inputRef}
        id={id}
        type="search"
        placeholder="Search games"
        value={value}
        autoComplete="off"
        onChange={(e) => update(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            update('');
            setOpen(false);
          }
        }}
      />
      <button
        type="button"
        className="search__close"
        aria-label="Close search"
        onClick={() => {
          update('');
          setOpen(false);
        }}
      >
        ×
      </button>
    </form>
  );
}

/**
 * Slim icon rail on computers (labels slide out on hover, like CrazyGames); a full list in the phone drawer.
 * Individual games are reached from the homepage rows and category pages, so they aren't listed here.
 */
function SideNav() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  // The active list: a category or mode page (/games/<slug>), or an old-style ?c= filter.
  const listing = findListing(/^\/games\/([^/]+)/.exec(pathname)?.[1]);
  const filter = listing ? ('category' in listing.filter ? listing.filter.category : listing.filter.mode) : pathname === '/' ? params.get('c') : null;
  const currentGame = CATALOG.find((g) => pathname === g.path || pathname.startsWith(`${g.path}/`));

  const item = (to: string, icon: IconName, label: string, active: boolean) => (
    <li key={to}>
      <Link to={to} className={`nav-item${active ? ' is-active' : ''}`} aria-current={active ? 'page' : undefined} title={label}>
        <span className="nav-item__icon">
          <NavIcon name={icon} />
        </span>
        <span className="nav-item__label">{label}</span>
      </Link>
    </li>
  );

  return (
    <nav className="sidenav__nav" aria-label="Games">
      <ul className="nav-list">
        {item('/', 'home', 'Home', pathname === '/' && !filter && !params.get('q'))}
        {item('/games/singleplayer', 'solo', 'Singleplayer', filter === 'solo')}
        {item('/games/2-player', 'multi', 'Multiplayer', filter === 'multi')}
      </ul>
      <hr className="nav-sep" />
      <ul className="nav-list">
        {CATEGORIES.map((c) => item(`/games/${categoryListing(c.id)?.slug ?? ''}`, c.id, c.label, filter === c.id || currentGame?.category === c.id))}
      </ul>
      <hr className="nav-sep" />
      <ul className="nav-list">
        {item('/guides', 'book', 'Guides', pathname.startsWith('/guides'))}
        {item('/updates', 'news', "What's New", pathname === '/updates')}
      </ul>
    </nav>
  );
}

/** Site frame: left navigation (a drawer on small screens), search and sound controls. */
export function SiteShell({ children }: { children: ReactNode }) {
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();

  useEffect(() => setDrawer(false), [location.pathname, location.search]);
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawer(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer]);

  return (
    <div className={`shell${location.pathname.startsWith('/play/') ? ' shell--game' : ''}${drawer ? ' shell--drawer' : ''}`}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <header className="topbar">
        <button type="button" className="tool-btn topbar__menu" aria-label="Open menu" aria-expanded={drawer} aria-controls="sidenav" onClick={() => setDrawer(true)}>
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
        <Link to="/" className="topbar__brand">
          <LogoMark size={30} />
          SuperFun<span className="brand__ubg">UBG</span>
        </Link>
        <div className="topbar__tools">
          <HeaderActions />
          <SearchBox />
          <SoundToggle />
        </div>
      </header>

      <aside id="sidenav" className="sidenav" aria-label="Site navigation">
        <div className="sidenav__head">
          <Brand />
          <button type="button" className="tool-btn sidenav__close" aria-label="Close menu" onClick={() => setDrawer(false)}>
            ×
          </button>
        </div>
        <SideNav />
        <AdSlot placement="sidebar" />
        <p className="sidenav__legal">
          <Link to="/privacy">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms">Terms</Link>
        </p>
      </aside>
      {drawer && <div className="scrim" onClick={() => setDrawer(false)} aria-hidden="true" />}

      <div className="shell__main">
        <div className="toolbar">
          <HeaderActions />
          <SearchBox />
          <SoundToggle />
        </div>
        {children}
        <footer className="site-footer">
          <span>© {new Date().getFullYear()} {LEGAL.operator}</span>
          <Link to="/about">About</Link>
          <Link to="/guides">Guides</Link>
          <Link to="/updates">What's New</Link>
          <Link to="/parents">Parents &amp; Teachers</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </footer>
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <path d="M15.5 15.5L21 21" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
